import { StrictMode } from "react"
import { createRoot, type Root } from "react-dom/client"
import { ArchieWidget } from "@/components/ArchieWidget"
import { ErrorBoundary } from "@/components/ErrorBoundary"
import { ToastHost } from "@/components/ToastHost"
import type { ToastInput } from "@/types/toast"

// Global React shell mounted on every layout. Owns toasts, and Archie when enabled.
// Prefer keeping the root alive across Turbo visits (see data-turbo-permanent
// on #toast-root) so in-flight toast state survives Drive navigations.
let reactRoot: Root | null = null
let mountedEl: HTMLElement | null = null
let pendingToasts: ToastInput[] = []

function signedInFromRoot(el: HTMLElement): boolean {
  return el.dataset.signedIn === "true"
}

function archieEnabledFromRoot(el: HTMLElement): boolean {
  return el.dataset.archieEnabled === "true"
}

function parseInitialToasts(el: HTMLElement): ToastInput[] {
  const raw = el.dataset.toasts
  if (!raw) return []
  try {
    const parsed = JSON.parse(raw) as unknown
    return Array.isArray(parsed) ? (parsed as ToastInput[]) : []
  } catch {
    return []
  }
}

function flushPendingToasts(): void {
  if (pendingToasts.length === 0) return
  const toasts = pendingToasts
  pendingToasts = []
  toasts.forEach((toast) => window.toast?.show(toast))
}

function unmount(): void {
  if (!reactRoot) return
  reactRoot.unmount()
  reactRoot = null
  mountedEl = null
}

function mount(): void {
  const rootEl = document.getElementById("toast-root")
  if (!rootEl) {
    unmount()
    return
  }

  // Same permanent node still hosts the React tree — only hydrate flash.
  if (reactRoot && mountedEl === rootEl && rootEl.isConnected) {
    parseInitialToasts(rootEl).forEach((toast) => window.toast?.show(toast))
    flushPendingToasts()
    return
  }

  unmount()

  mountedEl = rootEl
  reactRoot = createRoot(rootEl)
  reactRoot.render(
    <StrictMode>
      <ErrorBoundary>
        <ToastHost initialToasts={parseInitialToasts(rootEl)} />
        {archieEnabledFromRoot(rootEl) && (
          <ArchieWidget signedIn={signedInFromRoot(rootEl)} />
        )}
      </ErrorBoundary>
    </StrictMode>
  )
  // Defer so ToastHost can register window.toast.
  queueMicrotask(() => flushPendingToasts())
}

// Capture flash from the incoming page before Turbo discards its toast-root
// (permanent elements keep the previous node, including stale data-toasts).
document.addEventListener("turbo:before-render", (event) => {
  const customEvent = event as CustomEvent<{ newBody?: HTMLElement }>
  const newBody = customEvent.detail?.newBody
  const incoming = newBody?.querySelector<HTMLElement>("#toast-root")
  if (!incoming) return
  const toasts = parseInitialToasts(incoming)
  if (toasts.length > 0) pendingToasts = toasts
})

document.addEventListener("turbo:load", mount)
document.addEventListener("turbo:before-cache", () => {
  // Keep the permanent shell mounted so toast state survives Drive visits.
  // Only tear down when the node is going away entirely (no toast-root).
  if (!document.getElementById("toast-root")) unmount()
})

// turbo:load does not fire when this entry is first evaluated after a head-script
// insert on an in-flight Turbo visit, so mount immediately too.
mount()
